import {
	getPickupPoints,
	getProductsWithCouriers,
} from '@/API/repositories/lms';
import { sendMailByMailType } from '@/API/repositories/mail';
import {
	createOrderWithShipping,
	markOrdertAsConsultantDecision,
	sendOnlinePaymentLink,
	updateOrderByConsultant,
} from '@/API/repositories/order';
import {
	markInBoundCallAsConfirmed,
	markInboundCallAsInformed,
	markInboundCallAsResigned,
	markInBoundCallAsShiftOrder,
	markOnlinePaymentAsConfirmed,
} from '@/API/repositories/queue';
import userManager from '@/API/userManager';
import { CURRIER_TYPES } from '@/common/constants/curierTypes';
import { MIN_SHIFT_OFFSET } from '@/common/constants/minShiftOffset';
import { checkIfCommentAdded } from '@/common/functions/checkIfCommentAdded';
import { formatToExactTimeDate } from '@/common/functions/dateFormater';
import { getPostsMap } from '@/common/functions/getPostsMap';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { useCheckShippingLimit } from '@/common/hooks/useCheckShippingLimit';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import { useTranslationContext } from '@/common/hooks/useTranslationContext';
import * as moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';

const useOrderEditInbound = ({
	order,
	country,
	comments,
	setShow,
	contact,
	market,
	create,
}) => {
	const { hasUnfilledRequest, makeRequest } = useRequestsContext();
	const { addMessage } = useMessageQueueContext();
	const { translation } = useTranslationContext();
	const {
		commonData: { queues, products },
		options: { productsOptions },
		COUNTRIES,
	} = useCommonDataContext();

	const defaultShiftDate = moment().add(1, 'days').format('YYYY-MM-DDTHH:mm');
	const currentUserId = userManager.getUser().id;

	const nameRef = useRef();
	const emailRef = useRef();
	const phoneRef = useRef();
	const suggestedDeliveryDateRef = useRef();
	const streetRef = useRef();
	const postalCodeRef = useRef();
	const cityRef = useRef();

	const [selectedPoint, setSelectedPoint] = useState();
	const [pointsOptions, setPointsOptions] = useState();
	const [product, setProduct] = useState(order?._product);
	const [showPaymentConfirm, setShowPaymentConfirm] = useState();
	const [selectedCountry, setSelectedCountry] = useState(country);
	const [showExtraShiftConfirm, setShowExtraShiftConfirm] = useState(false);
	const [shiftDate, setShiftDate] = useState(defaultShiftDate);
	const [selectedOption, setSelectedOption] = useState();
	const [selectedOptionPrice, setSelectedOptionPrice] = useState();
	const [itemsError, setItemsError] = useState();
	const [selectedCourier, setSelectedCourier] = useState();
	const [courierOptions, setCourierOptions] = useState();
	const [productsWithCouriers, setProductsWithCouriers] = useState();
	const [showConfirm, setShowConfirm] = useState();
	const [type, setType] = useState();
	const [chosenVariants, setChosenVariants] = useState(
		order.order_items?.map((order, i) => ({
			_id: i + 1,
			name: product?.variants.find(
				(variant) => variant.short === order.product_variant,
			)?.name,
			value: order.product_variant,
			price: order.price,
		})) ?? [],
	);

	const { checkShippingLimit, minLimit, threeWeeksFromNowShippingLimit } =
		useCheckShippingLimit();

	const paymentsData = useMemo(() => {
		return order?.payment?.map((o, i) => (
			<tr>
				<td>{i + 1}.</td>
				<td>
					{(o.created_at && formatToExactTimeDate(o.created_at)) || '----'}
				</td>
				<td>{o.status || '----'}</td>
				<td>
					{(o.status_canceled_date &&
						formatToExactTimeDate(o.status_canceled_date)) ||
						'----'}
				</td>
				<td>
					{(o.status_complated_date &&
						formatToExactTimeDate(o.status_complated_date)) ||
						'----'}
				</td>
			</tr>
		));
	}, [order]);

	const wasCommentAdded = useMemo(
		() => checkIfCommentAdded(currentUserId, comments),
		[currentUserId, comments],
	);

	const availableProductsOptions = useMemo(() => {
		const currentUserId = userManager.getUser().id;

		const userQueues = queues.filter((queue) =>
			queue.consultants.includes(currentUserId),
		);

		const availableProducts = Array.from(
			new Set(userQueues.map((queue) => queue.product)),
		);
		return productsOptions.filter((p) => availableProducts.includes(p.value));
	}, [queues, productsOptions]);

	const handleMarkAsConsultantDecision = async (e) => {
		e.preventDefault();
		switch (type) {
			case 'resign':
				handleResign(e);
				break;
			case 'confirm':
				handleCofnirm(e);
				break;
			case 'shift':
				handleShift(e);
				break;
			case 'informed':
				handleInformed(e);
				break;
			default:
				break;
		}
	};

	const handleInformed = async () => {
		const response = await makeRequest(
			markInboundCallAsInformed.bind(null, order._id),
		);

		if (!response?.data) {
			return addMessage(
				'You are not allowed to update this order, please, quickly contact CC Manager',
				'error',
			);
		}

		setShowConfirm(() => false);
		setShow(() => false);
	};

	const handleResign = async (e) => {
		const response = await makeRequest(
			markInboundCallAsResigned.bind(null, order._id),
		);

		if (!response?.data) {
			return addMessage(
				'You are not allowed to update this order, please, quickly contact CC Manager',
				'error',
			);
		}

		setShowConfirm(() => false);
		setShow(() => false);
	};

	const handleCofnirm = async (e) => {
		if (chosenVariants.length === 0) {
			setItemsError(() => 'item');
			return;
		}

		if (contact.country === 'cz' && !contact.hasPromotion) {
			const price = chosenVariants.reduce((prev, next) => prev + next.price, 0);

			if (price < 1960 * 5) {
				setItemsError(() => 'bad price to promotion');
				return;
			}
		}

		if (
			selectedCourier?.rest?.type === CURRIER_TYPES.PICKUP &&
			!selectedPoint
		) {
			return addMessage('Please Pick Pickup Point!', 'error');
		}

		await handleSaveOrder(e);
		const response = await makeRequest(
			markInBoundCallAsConfirmed.bind(null, order._id),
		);

		if (!response?.data) {
			return addMessage(
				'You are not allowed to update this order, please, quickly contact CC Manager',
				'error',
			);
		}

		setShowConfirm(() => false);
		setShow(() => false);
	};

	const handleShift = async (e) => {
		if (
			MIN_SHIFT_OFFSET >
			new Date(shiftDate).getTime() - new Date().getTime()
		) {
			return addMessage(
				'Shift date should be at least 10 minutes from now',
				'error',
			);
		}
		const response = await makeRequest(
			markInBoundCallAsShiftOrder.bind(
				null,
				order._id,
				new Date(shiftDate).toISOString(),
			),
		);

		if (!response?.data) {
			return addMessage(
				'You are not allowed to update this order, please, quickly contact CC Manager',
				'error',
			);
		}

		setShowConfirm(() => false);
		setShow(() => false);
	};

	const saveOrder = async (onlinePayment = false) => {
		const deliveryDate = suggestedDeliveryDateRef.current
			? moment(suggestedDeliveryDateRef.current.value)
			: moment().add(2, 'days');

		const isShippingDateValid = checkShippingLimit(deliveryDate);

		if (!isShippingDateValid) {
			return;
		}

		const shipping = Object();
		shipping.full_name = nameRef.current?.value ?? contact.full_name;
		shipping.email = emailRef.current?.value ?? contact.email;
		shipping.phone_number = phoneRef.current?.value ?? contact.phone_number;
		shipping.street = streetRef.current?.value ?? contact.street;
		shipping.postal_code = postalCodeRef.current?.value ?? contact.postal_code;
		shipping.city = cityRef.current?.value ?? contact.city;
		shipping.country = selectedCountry.value;
		shipping.curier_lms_id = selectedCourier?.value;
		shipping.pickup_point = selectedPoint?.value;
		shipping.suggested_delivery_date = deliveryDate;

		order.currency = market.currency;
		order.market = market._id;
		order.product = product._id;
		order.items = chosenVariants;
		order.shipping = shipping;
		order.isOnlinePayment = onlinePayment;

		if (create) {
			if (chosenVariants.length > 0) {
				return makeRequest(
					createOrderWithShipping.bind(null, contact._id, order),
				);
			}
		} else {
			return makeRequest(updateOrderByConsultant.bind(null, order._id, order));
		}
	};

	const handleSaveOrder = async (e) => {
		e.preventDefault();

		const response = await saveOrder();

		if (response?.data) {
			setShow(() => false);
		} else {
			return addMessage(
				'You are not allowed to update this order, please, quickly contact CC Manager',
				'error',
			);
		}
	};

	const handleAddProduct = (e) => {
		e.preventDefault();

		if (selectedOption && selectedOptionPrice) {
			setChosenVariants((prev) => [
				...chosenVariants,
				{
					_id: chosenVariants.length + 1,
					name: selectedOption.label,
					value: selectedOption.value,
					price: selectedOptionPrice.value,
				},
			]);
		}
	};

	const handleDelteProductItem = (e, _id) => {
		e.preventDefault();
		let data = chosenVariants.filter((item) => item._id !== _id);
		let id = 1;
		data = data.map((data) => {
			data._id = id;
			id++;
			return data;
		});
		setChosenVariants(() => data);
	};

	const loadCouriers = async () => {
		setSelectedCourier(() => null);
		setCourierOptions(() => []);

		const response = await makeRequest(getProductsWithCouriers);

		if (response?.data) {
			setProductsWithCouriers(response.data);
		}
	};

	const handleSetCouriersData = () => {
		if (!productsWithCouriers) {
			return;
		}

		const currentProductWithCourier = productsWithCouriers.find(
			(productWithCourier) => product?.short === productWithCourier.short,
		);

		if (!currentProductWithCourier) {
			return;
		}

		const postsMap = getPostsMap(currentProductWithCourier);

		const options = Array.from(postsMap).map(([label, value]) => ({
			label,
			value: value._id,
			rest: value,
		}));

		setCourierOptions(options);

		setSelectedCourier(
			options.find(
				(option) => option.value === order?.shipping?.curier_lms_id,
			) || options[0],
		);
	};

	const handleSaveOrderOnlinePayment = async () => {
		const createdOrder = await saveOrder(true);

		if (!createdOrder.data) {
			return addMessage('ERROR While creating order', 'error');
		}

		const paymentLinkResponse = await makeRequest(
			sendOnlinePaymentLink.bind(null, createdOrder.data._id),
		);

		if (!paymentLinkResponse.data) {
			return addMessage('ERROR while sending payment link', 'error');
		}

		addMessage('Success', 'success');

		setShowConfirm(() => false);
		setShow(() => false);
	};

	const selectProduct = (product) => {
		setProduct(products.find((p) => p._id === product.value));
	};

	const handleFetchPickupPoints = async () => {
		const selectedCourierId = selectedCourier?.value;
		const selectedCourierProvider = selectedCourier?.rest?.provider;

		const pickupPointsResponse = await makeRequest(
			getPickupPoints.bind(null, selectedCourierId, selectedCourierProvider),
		);
		const pickupPointsData = pickupPointsResponse.data ?? [];
		const pointsOptions = pickupPointsData.map((point) => {
			return {
				label: `${point.address} ${point.city} ${point.carrierType}`,
				value: point.id,
			};
		});

		const pickupPoint = order?.shipping?.pickup_point_id;

		if (pickupPoint) {
			const foundAddress =
				pointsOptions.find((p) => p.value == pickupPoint) || null;
			setSelectedPoint(() => foundAddress);
		}

		setPointsOptions(() => pointsOptions);
	};

	useEffect(() => {
		loadCouriers();
	}, [product]);

	useEffect(() => {
		handleSetCouriersData();
	}, [productsWithCouriers]);

	useEffect(() => {
		if (selectedCourier?.rest?.type === CURRIER_TYPES.PICKUP) {
			setSelectedPoint(() => null);
			setPointsOptions(() => null);

			handleFetchPickupPoints();
		}
	}, [selectedCourier]);

	const isLoading = hasUnfilledRequest(
		createOrderWithShipping,
		getProductsWithCouriers,
		updateOrderByConsultant,
		markOrdertAsConsultantDecision,
		sendMailByMailType,
		markOnlinePaymentAsConfirmed,
		sendOnlinePaymentLink,
	);

	const suggestedDeliveryDateInit = order.shipping?.suggested_delivery_date
		? moment(order.shipping.suggested_delivery_date).format('YYYY-MM-DD')
		: moment().add(2, 'days').format('YYYY-MM-DD');

	return {
		isLoading,
		translation,
		product,
		handleSaveOrder,
		nameRef,
		emailRef,
		phoneRef,
		setType,
		setShowConfirm,
		wasCommentAdded,
		setShowPaymentConfirm,
		suggestedDeliveryDateRef,
		streetRef,
		postalCodeRef,
		cityRef,
		selectedCountry,
		setSelectedCountry,
		COUNTRIES,
		selectedCourier,
		setSelectedCourier,
		courierOptions,
		pointsOptions,
		selectedPoint,
		setSelectedPoint,
		selectProduct,
		availableProductsOptions,
		selectedOption,
		setSelectedOption,
		selectedOptionPrice,
		setSelectedOptionPrice,
		handleAddProduct,
		chosenVariants,
		handleDelteProductItem,
		showConfirm,
		type,
		setShiftDate,
		shiftDate,
		setShowExtraShiftConfirm,
		handleMarkAsConsultantDecision,
		showExtraShiftConfirm,
		itemsError,
		setItemsError,
		showPaymentConfirm,
		suggestedDeliveryDateInit,
		paymentsData,
		handleSaveOrderOnlinePayment,
		minLimit,
		threeWeeksFromNowShippingLimit,
	};
};

export default useOrderEditInbound;
